import React, { useState, useEffect } from 'react';
import { Row, Col, Form, InputGroup, Button, Alert, Dropdown, DropdownButton, Spinner, FormControl } from 'react-bootstrap';
import { fetchWeerepo, postWeerepo } from '../../../firebase-config';
import { useNavigate } from 'react-router-dom';

function WeeRepoPage({ employee }) {
    const draftKey = `weerepo_draft_${employee.id}`;

    const loadDraft = () => {
        try {
            const saved = localStorage.getItem(draftKey);
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    };

    const draft = loadDraft();

    useEffect(() => {
        document.title = "ウィーレポ";
    }, []);
    const navigate = useNavigate();

    const [workEmoji, setWorkEmoji] = useState(draft?.workEmoji ?? 'relieved');
    const [mentalEmoji, setMentalEmoji] = useState(draft?.mentalEmoji ?? 'smile');

    const [works, setWorks] = useState(draft?.works ?? ['']);
    const [insideWorks, setInsideWorks] = useState(draft?.insideWorks ?? ['']);

    const [content, setContent] = useState(draft?.content ?? '');
    const [hasDraft, setHasDraft] = useState(!!draft);

    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [getLoading, setGetLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const workEmojis = [
        { label: "😌", value: "relieved", state: "落ち着いている", score: 5 },
        { label: "🙂", value: "slightly_smiling_face", state: "やや落ち着いている", score: 4 },
        { label: "🙁", value: "frowning", state: "普通", score: 3 },
        { label: "😔", value: "sad", state: "やや忙しい", score: 2 },
        { label: "🤢", value: "disgust", state: "かなり忙しい", score: 1 },
    ];

    const mentalEmojis = [
        { label: "😆", value: "laughing", state: "非常に良い", score: 5 },
        { label: "😀", value: "smile", state: "良い", score: 4 },
        { label: "🙂", value: "slightly_smiling_face", state: "少し良い", score: 3.5 },
        { label: "🙁", value: "frowning", state: "普通", score: 3 },
        { label: "😟", value: "worried", state: "少し悪い", score: 2.5 },
        { label: "😩", value: "tired", state: "悪い", score: 2 },
        { label: "🤮", value: "face_vomiting", state: "とても悪い", score: 1 }
    ];

    useEffect(() => {
        localStorage.setItem(draftKey, JSON.stringify({ workEmoji, mentalEmoji, works, insideWorks, content }));
        setHasDraft(true);
    }, [workEmoji, mentalEmoji, works, insideWorks, content]);

    const clearDraft = () => {
        localStorage.removeItem(draftKey);
        setHasDraft(false);
    };

    const addWork = () => {
        setWorks([...works, '']);
    };

    const removeWork = (index) => {
        setWorks(works.filter((_, i) => i !== index));
    };

    const addInsideWork = () => {
        setInsideWorks([...insideWorks, '']);
    };

    const removeInsideWork = (index) => {
        setInsideWorks(insideWorks.filter((_, i) => i !== index));
    };

    const handlePrevData = async () => {
        setGetLoading(true);

        try {
            const response = await fetchWeerepo(employee.id, 1, 0);
            if (!response || !response.results || response.results.length === 0) {
                console.warn("No previous data available.");
                return;
            }
            const data = response.results[0]?.data;

            if (data) {
                setWorks(data.works);
                setInsideWorks(data.insideWorks);
            }
        } catch (error) {
            console.error("Error fetching previous data:", error);
        } finally {
            setGetLoading(false);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const selectedWorkEmoji = workEmojis.find(emoji => emoji.value === workEmoji);
            const selectedMentalEmoji = mentalEmojis.find(emoji => emoji.value === mentalEmoji);

            await postWeerepo(
                employee.id,
                employee.slackMemberId,
                employee.legechId,
                selectedWorkEmoji,
                selectedMentalEmoji,
                works,
                insideWorks,
                content
            );
            clearDraft();
            setShowSuccess(true);
            setShowError(false);
            setContent("");
        } catch (error) {
            console.error("Error submitting data:", error);
            setShowSuccess(false);
            setShowError(true);
            setErrorMessage(error.response?.data?.details || 'Unknown error occurred');
        } finally {
            setLoading(false);
        }
    };


    function getViewLabel(emojiJson) {
        return emojiJson.label + " " + emojiJson.state;
    }

    return (
        <Row>
            <Row className='mt-4'>
                <Col xs={6} >
                    <h1>ウィーレポ</h1>
                </Col>
                <Col xs={3} className='text-end'>
                    <Button onClick={() => handlePrevData()} style={{ cursor: 'pointer' }} variant="light">
                        {getLoading ? '取得中...' : '前回分取得'}
                    </Button>

                    {getLoading && <div><Spinner animation="border" variant="dark" /></div>}
                </Col>
                <Col xs={3} className='text-end'>
                    <Button onClick={() => {
                        navigate('/report/weerepo/archives', { state: { myEmployee: employee, employee: employee } });
                    }}
                        style={{ cursor: 'pointer' }} variant="light">
                        アーカイブ
                    </Button>
                </Col>
            </Row>
            {hasDraft && (
                <Row className='mt-2'>
                    <Col>
                        <Alert variant="info" className="py-2 d-flex align-items-center justify-content-between mb-0">
                            <span style={{ fontSize: '0.85rem' }}>下書きを自動保存しています</span>
                            <Button size="sm" variant="outline-secondary" onClick={clearDraft}>
                                下書きを削除
                            </Button>
                        </Alert>
                    </Col>
                </Row>
            )}

            <Form onSubmit={handleSubmit} className='mb-3'>
                <Row className='mt-4'>
                    <Col xs={6} >
                        <Form.Group controlId="formWorkEmoji" className="mb-3">
                            <Form.Label>
                                業務量
                            </Form.Label>
                            <DropdownButton
                                variant="outline-secondary"
                                title={getViewLabel(workEmojis.find(e => e.value === workEmoji))}
                            >
                                {workEmojis.map(e => (
                                    <Dropdown.Item
                                        key={e.value} onClick={() => setWorkEmoji(e.value)}
                                    >
                                        {getViewLabel(e)}
                                    </Dropdown.Item>
                                ))}
                            </DropdownButton>
                        </Form.Group>

                    </Col>
                    <Col xs={6} >

                        <Form.Group controlId="formMentalEmoji" size="lg">
                            <Form.Label>
                                メンタル
                            </Form.Label>
                            <DropdownButton
                                variant="outline-secondary"
                                title={getViewLabel(mentalEmojis.find(e => e.value === mentalEmoji))}
                            >
                                {mentalEmojis.map(e => (
                                    <Dropdown.Item
                                        key={e.value} onClick={() => setMentalEmoji(e.value)}
                                    >
                                        {getViewLabel(e)}
                                    </Dropdown.Item>
                                ))}
                            </DropdownButton>
                        </Form.Group>

                    </Col>
                </Row>

                <Form.Group controlId="formMessage" className='mt-3'>
                    <Form.Label>
                        現場
                    </Form.Label>

                    {works.map((work, index) => (
                        <InputGroup className="mb-3" key={index}>
                            <FormControl
                                value={work}
                                onChange={e =>
                                    setWorks([...works.slice(0, index), e.target.value, ...works.slice(index + 1)])
                                }
                            />
                            <Button variant="outline-danger" onClick={() => removeWork(index)}>削除</Button>
                        </InputGroup>
                    ))}
                    <div className="gap-2 mt-3">
                        <Button onClick={addWork} variant="secondary">
                            追加
                        </Button>
                    </div>
                </Form.Group>


                <Form.Group controlId="formMessage" className='mt-3'>
                    <Form.Label>
                        自社
                    </Form.Label>

                    {insideWorks.map((insideWork, index) => (
                        <InputGroup className="mb-3" key={index}>
                            <FormControl
                                value={insideWork}
                                onChange={e =>
                                    setInsideWorks([...insideWorks.slice(0, index), e.target.value, ...insideWorks.slice(index + 1)])
                                }
                            />
                            <Button variant="outline-danger" onClick={() => removeInsideWork(index)}>削除</Button>
                        </InputGroup>
                    ))}
                    <div className="gap-2 mt-3">
                        <Button onClick={addInsideWork} variant="secondary">
                            追加
                        </Button>
                    </div>
                </Form.Group>

                <Form.Group controlId="formMessage" className='mt-3'>
                    <Form.Control
                        as="textarea"
                        rows={8}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="連絡事項"
                        required
                    />
                </Form.Group>

                <div className="d-grid gap-2 mt-5">
                    <Button variant="primary" type="submit" size="lg" disabled={loading}>
                        {loading ? '送信中...' : '送信'}
                    </Button>

                    {loading && <Spinner animation="border" variant="dark" />}
                </div>
            </Form>

            {showSuccess && <Alert variant="success">送信しました。</Alert>}
            {showError && <Alert variant="danger">送信失敗しました。{errorMessage}</Alert>}
        </Row>
    );
}

export default WeeRepoPage;
